import React from "react";
import { useForm, Controller } from "react-hook-form";
import { Button, cn, Select, toast } from "@coninsa/core";
import { queryString, storage } from "@coninsa/utils";

import Alert from "../components/Alert";
import { T_fieldSettings } from "../api";

type T_props = {
  onSuccess: () => void;
  fieldSettings: T_fieldSettings;
};

export type T_form = {
  is_owner: string;
  cod_ciudad_documento: { value: string; label: string };
  cod_cliente: string;
  cod_tipo_cliente: string;
  cod_tipo_documento: { value: string; label: string };
  dir_cliente: string;
  email: string;
  primer_apellido: string;
  primer_nombre: string;
  segundo_apellido: string;
  segundo_nombre: string;
};

function Step1({ onSuccess, fieldSettings }: T_props) {
  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    register,
  } = useForm<T_form>({
    defaultValues: async () => {
      return JSON.parse(localStorage.getItem("consignation.client") ?? "{}");
    },
  });

  const user = storage.get("consignation.user");
  const alreadyRegisteredUser = user?.isNew;

  const onSubmit = async (data: T_form) => {
    storage.set("consignation.client", data);
    const user = storage.get("consignation.user");
    const cellphone = user.cellphone;

    if (cellphone) {
      // toast.success({ message: "Cliente registrado" });
      queryString.set("client", data.cod_cliente);
      onSuccess();
    }
  };

  return (
    <div className="co-card relative mx-auto max-w-4xl">
      <Alert className="is-primary">
        <p className="mb-3">¡Hola!</p>
        <div className="mb-3">
          <p className={cn(!alreadyRegisteredUser && "font-bold")}>
            Ya tenemos tu número, pero necesitamos que termines de llenar los
            datos.
          </p>
          <p className={cn(!alreadyRegisteredUser && "font-bold")}>
            ¿Vas a subir un nuevo inmueble? No olvides completar toda la info
            para que salga perfecto.
          </p>
        </div>

        <span
          className={cn(
            !alreadyRegisteredUser && "hidden",
            alreadyRegisteredUser && "block is-highlight"
          )}
        >
          ¡Que bueno poder promocionar tu inmueble de vivienda en arriendo!
        </span>
        <p>
          Recuerda diligenciar la información completa y de forma adecuada, así
          tu proceso de promoción del inmueble será más rápido.
        </p>
      </Alert>

      <h3 className="mb-4 text-[17px] font-semibold lg:mb-5 lg:text-lg">
        Registra tus datos personales para continuar con el proceso
      </h3>

      <form className="co-form flex gap-y-7" onSubmit={handleSubmit(onSubmit)}>
        <fieldset className="form-group are-radios">
          <legend>¿Qué eres del inmueble que promocionarás?</legend>
          <div className="form-items flex-col md:flex-row">
            <div className="form-control is-radio">
              <input
                {...register("is_owner")}
                type="radio"
                id="person_owner"
                value="2"
              />
              <label htmlFor="person_owner">Soy el propietario</label>
            </div>
            <div className="form-control is-radio">
              <input
                {...register("is_owner")}
                type="radio"
                id="person_proxy"
                value="1"
              />
              <label htmlFor="person_proxy">Soy un conocido</label>
            </div>
            <div className="form-control is-radio">
              <input
                {...register("is_owner")}
                type="radio"
                id="person_attorney"
                value="0"
              />
              <label htmlFor="person_proxy">Soy un conocido / apoderado</label>
            </div>
          </div>
        </fieldset>

        <div className="flex flex-col gap-4 md:flex-row">
          <div className="form-control grow">
            <label className="is-required">Primer nombre</label>
            <input {...register("primer_nombre")} type="text" required />
          </div>
          <div className="form-control grow">
            <label>Segundo nombre</label>
            <input {...register("segundo_nombre")} type="text" />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="form-control grow">
            <label className="is-required">Primer apellido</label>
            <input {...register("primer_apellido")} type="text" required />
          </div>

          <div className="form-control grow">
            <label className="is-required">Segundo apellido</label>
            <input {...register("segundo_apellido")} type="text" required />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="form-control">
            <label className="is-required">Correo electrónico</label>
            <input {...register("email")} type="email" required />
          </div>
          <div className="form-control">
            <label htmlFor="client_type" className="is-required">
              Tipo de cliente
            </label>
            <select {...register("cod_tipo_cliente")} id="client_type" required>
              <option value="">- Seleccione una opción -</option>
              {fieldSettings?.client?.types?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="form-control">
            <label htmlFor="document_type" className="is-required">
              Tipo de documento
            </label>
            <Controller
              control={control}
              name="cod_tipo_documento"
              render={({ field: { value, onChange } }) => (
                <Select
                  options={fieldSettings?.client?.documentTypes}
                  onChange={(option) => onChange(option)}
                  value={value}
                  placeholder="Seleccione una opción"
                  required
                />
              )}
            />
          </div>

          <div className="form-control">
            <label className="is-required">Numero de documento</label>
            <input {...register("cod_cliente")} type="text" required />
          </div>
        </div>

        <div className="flex gap-2 md:flex-row md:justify-end">
          <Button
            loading={isSubmitting}
            className="co-button is-progress-default is-large w-full md:w-auto"
          >
            Continuar
          </Button>
        </div>
      </form>
    </div>
  );
}

export default Step1;
